import React, { useRef, useEffect, useState } from "react";
import cn from "@/utils/class-names";
import { ITrackingTimeline } from "@/utilities/types/TrackingTimeline.ts";
import isValidDate from "@/utils/isValidDate.ts";
import dayjs from "dayjs";
import {
  DocumentTextIcon,
  ClockIcon,
  LinkIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import {
  EyeIcon as EyeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
} from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";

export interface ImprovedTimelineProps {
  timelineData: ITrackingTimeline;
  submittedFromDomain: string;
  isLoading?: boolean;
}

// Loading skeleton component
const TimelineLoadingSkeleton = () => (
  <div className="w-full max-w-4xl mx-auto">
    <div className="flex flex-col md:flex-row gap-4">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="flex-1 animate-pulse">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full mr-4"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="h-3 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Empty state component
const TimelineEmptyState = () => (
  <div className="w-full max-w-4xl mx-auto text-center py-12">
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
      <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        No Timeline Data
      </h3>
      <p className="text-gray-600">
        No tracking information is available for this lead yet.
      </p>
    </div>
  </div>
);

// Event icon component
const EventIcon = ({ type }: { type: string }) => {
  const iconClass = "w-6 h-6 text-white";

  const iconMap = {
    visit: EyeIconSolid,
    enquiry: DocumentTextIconSolid,
    completion: CheckCircleIconSolid,
  };

  const IconComponent = iconMap[type as keyof typeof iconMap] || ClockIcon;

  return <IconComponent className={iconClass} />;
};

// Event badge component
const EventBadge = ({ type }: { type: string }) => {
  const badges = {
    visit: { label: "Visit", color: "bg-blue-100 text-blue-800" },
    enquiry: { label: "Enquiry", color: "bg-green-100 text-green-800" },
    completion: { label: "Completed", color: "bg-purple-100 text-purple-800" },
  };

  const badge = badges[type as keyof typeof badges] || {
    label: "Event",
    color: "bg-gray-100 text-gray-800",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
        badge.color
      )}
    >
      {badge.label}
    </span>
  );
};

const ImprovedTimeline: React.FC<ImprovedTimelineProps> = ({
  timelineData,
  submittedFromDomain,
  isLoading = false,
}) => {
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  useEffect(() => {
    const calculateContentWidth = () => {
      if (timelineContainerRef.current) {
        const container = timelineContainerRef.current;
        const scrollWidth = container.scrollWidth;
        setContentWidth(scrollWidth);
      }
    };

    calculateContentWidth();

    // Recalculate on window resize
    window.addEventListener("resize", calculateContentWidth);

    return () => {
      window.removeEventListener("resize", calculateContentWidth);
    };
  }, [timelineData]);

  if (isLoading) {
    return <TimelineLoadingSkeleton />;
  }

  if (!timelineData?.timeline) {
    return <TimelineEmptyState />;
  }

  const timelineEvents = timelineData.timeline
    .filter((item) => item?.projectName)
    .map((event) => ({
      type: "visit",
      title: `Visited ${event.projectName}`,
      date: dayjs(new Date(event.visitedAt))
        .tz("Asia/Singapore")
        .format("MMM DD, YYYY hh:mm A"),
      name: event.projectName,
      submittedFromDomain: submittedFromDomain,
      enquiry: isValidDate(event.submittedAt),
      enquiryDate: isValidDate(event.submittedAt)
        ? dayjs(new Date(event.submittedAt!))
            .tz("Asia/Singapore")
            .format("MMM DD, YYYY hh:mm A")
        : null,
    }));

  if (timelineData?.endTime && isValidDate(timelineData.endTime)) {
    const lastEvent = timelineData.timeline[timelineData.timeline.length - 1];
    timelineEvents.push({
      type: "completion",
      title: "Lead Completed",
      date: dayjs(new Date(timelineData.endTime))
        .tz("Asia/Singapore")
        .format("MMM DD, YYYY hh:mm A"),
      name: lastEvent?.projectName || "",
      submittedFromDomain: submittedFromDomain,
      enquiry: false,
      enquiryDate: null,
    });
  }

  const timeline = timelineEvents;

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Timeline Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-900 rounded-lg">
              <ClockIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Lead Journey Timeline
              </h3>
              <p className="text-sm text-gray-600">
                Track the complete customer journey
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="relative overflow-x-auto">
        <div
          ref={timelineContainerRef}
          className="relative flex flex-col md:flex-row md:items-start md:justify-between space-y-8 md:space-y-0 md:space-x-8 py-4"
        >
          {/* Connecting Line */}
          <div
            className="absolute top-6 left-6 md:top-12 md:left-0 w-0.5 md:h-0.5 h-full bg-gradient-to-b md:bg-gradient-to-r from-blue-200 via-green-200 to-purple-200 z-0"
            style={{
              width: window.innerWidth >= 768 ? `${contentWidth}px` : "2px",
            }}
          ></div>

          {timeline.map((event, index) => {
            const eventType = event.enquiry ? "enquiry" : event.type;

            return (
              <div
                key={`${event.title}-${index}`}
                className="relative flex-1 flex items-start md:flex-col group"
              >
                {/* Timeline Marker */}
                <div className="flex-shrink-0 md:w-full md:flex md:justify-center mb-4 z-10">
                  <div
                    className={cn(
                      "p-3 rounded-full shadow-lg transition-all duration-300 group-hover:scale-110",
                      eventType === "visit" && "bg-gray-900",
                      eventType === "enquiry" && "bg-gray-900",
                      eventType === "completion" && "bg-gray-900"
                    )}
                  >
                    <EventIcon type={eventType} />
                  </div>
                </div>

                {/* Timeline Card */}
                <div
                  className={cn(
                    "ml-4 min-w-[230px] w-full md:max-w-fit md:ml-0 bg-white rounded-xl shadow-lg border border-gray-200 p-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-1",
                    eventType === "visit" && "hover:border-blue-300",
                    eventType === "enquiry" && "hover:border-green-300",
                    eventType === "completion" && "hover:border-purple-300"
                  )}
                >
                  <div className="flex items-center justify-between mb-3">
                    <EventBadge type={eventType} />
                  </div>

                  <Link
                    to={
                      event.submittedFromDomain.startsWith("http")
                        ? event.submittedFromDomain
                        : `https://${event.submittedFromDomain}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-1 items-center justify-start underline transition-colors mb-2"
                  >
                    <h4 className="font-bold text-gray-900 text-lg">
                      {event.name}
                    </h4>
                    <LinkIcon className="w-4 h-4" />
                  </Link>

                  <div className="space-y-2 text-sm">
                    {event.enquiry && (
                      <div className="flex items-center text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                        <DocumentTextIcon className="w-4 h-4 mr-2" />
                        <span className="font-medium">Enquiry Submitted</span>
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <CalendarDaysIcon className="w-4 h-4 mr-2" />
                      <span>{event.date}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImprovedTimeline;
